/**
 * Dashboard — default page for the owner role.
 * Shows today's KPIs vs yesterday, 30-day revenue trend, hourly pattern,
 * cash/card split, top products, and low-stock alerts.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { ArrowRight, BarChart3, CreditCard, Gift, Receipt, Users, Wallet } from 'lucide-react';
import type { Database } from '@cafe-pos/supabase';
import { fetchPaged } from '../fetchPaged.js';
import { wageForRange, type PayType, type PunchSpan, type RatePeriod } from '../lib/wages.js';
import { formatAmd, resolveAppLocale } from '../locale.js';
import { supabase } from '../supabase.js';
import { LowStockPanel } from '../components/LowStockPanel.js';
import {
  Button,
  Callout,
  Card,
  CardHeader,
  CHART_CURSOR,
  CHART_TICK,
  CHART_TOOLTIP,
  PageHeader,
  Stat,
  Stats,
  chartColor,
} from '../ui/index.js';

type IngredientRow = Database['public']['Tables']['ingredients']['Row'];

function dayStart(daysAgo: number): Date {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  d.setHours(0, 0, 0, 0);
  return d;
}

function dayLabel(d: Date, locale: string): string {
  return d.toLocaleDateString(locale, { day: '2-digit', month: '2-digit' });
}

/** Ease a KPI from its last value when live data arrives. First paint snaps. */
function useAnimatedNumber(value: number, ready: boolean): number {
  const [shown, setShown] = useState(value);
  const shownRef = useRef(value);
  const primed = useRef(false);

  useEffect(() => {
    if (!ready) {
      primed.current = false;
      shownRef.current = value;
      setShown(value);
      return;
    }
    if (!primed.current) {
      primed.current = true;
      shownRef.current = value;
      setShown(value);
      return;
    }
    const from = shownRef.current;
    const to = value;
    if (from === to) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      shownRef.current = to;
      setShown(to);
      return;
    }
    const start = performance.now();
    let frame = 0;
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / 480);
      const eased = 1 - (1 - t) ** 3;
      const next = from + (to - from) * eased;
      shownRef.current = next;
      setShown(next);
      if (t < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [value, ready]);

  return shown;
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface KpiValues {
  revenue: number;
  orders: number;
  pax: number;
  /** Total value of force-closed "free" orders (comped, not counted in revenue). */
  free: number;
  /** Card tips on paid orders. Kept out of revenue, same as Sales. */
  tips: number;
}

interface DayBucket {
  label: string;
  revenue: number;
}

interface HourBucket {
  label: string;
  orders: number;
}

interface PaySplit {
  method: 'cash' | 'card';
  value: number;
  color: string;
}

interface TopProduct {
  name: string;
  revenue: number;
}

interface DayStaff {
  id: string;
  name: string;
  wageAmd: number;
}

interface DayBookingMove {
  id: string;
  name: string;
  kind: 'arriving' | 'leaving';
  pending: boolean;
}

interface DayBookings {
  inHouse: number;
  arriving: number;
  leaving: number;
  movement: DayBookingMove[];
}

interface CatShare {
  /** Empty name means the line had no menu category. */
  name: string;
  value: number;
}

interface DashState {
  loading: boolean;
  error: string | null;
  today: KpiValues;
  yesterday: KpiValues;
  trend: DayBucket[];
  hourly: HourBucket[];
  paymentSplit: PaySplit[];
  topProducts: TopProduct[];
  lowStock: IngredientRow[];
  dayStaff: DayStaff[];
  /** Null when bookings are off for this client. */
  dayBookings: DayBookings | null;
  categoryShare: CatShare[];
}

const EMPTY_KPI: KpiValues = { revenue: 0, orders: 0, pax: 0, free: 0, tips: 0 };

const INITIAL: DashState = {
  loading: true,
  error: null,
  today: EMPTY_KPI,
  yesterday: EMPTY_KPI,
  trend: [],
  hourly: [],
  paymentSplit: [],
  topProducts: [],
  lowStock: [],
  dayStaff: [],
  dayBookings: null,
  categoryShare: [],
};

// ─── Data fetching ────────────────────────────────────────────────────────────

function asPayType(value: string | undefined): PayType {
  if (value === 'shift' || value === 'monthly') return value;
  return 'hourly';
}

function localIso(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

async function fetchDashboard(
  clientId: string,
  locale: string,
  bookingsEnabled: boolean,
): Promise<Omit<DashState, 'loading' | 'error'>> {
  if (!supabase) throw new Error('Supabase not configured');
  const db = supabase;

  const todayStart = dayStart(0);
  const tomorrowStart = dayStart(-1);
  const yesterdayStart = dayStart(1);
  const todayIso = localIso(todayStart);
  // 30-day window = today + previous 29 days, matching the trend chart buckets.
  const thirtyAgo = dayStart(29);
  const sevenAgo = dayStart(6);

  const [todayRes, yestRes, trendRows, hourlyRows, paymentRows, itemRows, ingRes, staffRes, rateRes, entryRes, bookingRes, productRes, todayItems] =
    await Promise.all(
    [
      // Paid orders drive the revenue KPIs; free (comped) force-closes are
      // fetched alongside so the dashboard can show what was given away.
      db
        .from('orders')
        .select('total, pax, status, close_reason, tip_amount')
        .eq('client_id', clientId)
        .or('status.eq.paid,close_reason.eq.free')
        .gte('paid_at', todayStart.toISOString()),

      db
        .from('orders')
        .select('total, pax, status, close_reason, tip_amount')
        .eq('client_id', clientId)
        .or('status.eq.paid,close_reason.eq.free')
        .gte('paid_at', yesterdayStart.toISOString())
        .lt('paid_at', todayStart.toISOString()),

      fetchPaged<{ paid_at: string | null; total: number }>((from, to) =>
        db
          .from('orders')
          .select('paid_at, total')
          .eq('client_id', clientId)
          .eq('status', 'paid')
          .gte('paid_at', thirtyAgo.toISOString())
          .order('paid_at', { ascending: true })
          .range(from, to),
      ),

      fetchPaged<{ paid_at: string | null }>((from, to) =>
        db
          .from('orders')
          .select('paid_at')
          .eq('client_id', clientId)
          .eq('status', 'paid')
          .gte('paid_at', sevenAgo.toISOString())
          .order('paid_at', { ascending: true })
          .range(from, to),
      ),

      fetchPaged<{ method: string; amount: number }>((from, to) =>
        db
          .from('payments')
          .select('method, amount')
          .eq('client_id', clientId)
          .eq('status', 'paid')
          .gte('created_at', thirtyAgo.toISOString())
          .order('created_at', { ascending: true })
          .range(from, to),
      ),

      fetchPaged<{
        name: string;
        unit_price: number;
        quantity: number;
        discount: number | null;
      }>((from, to) =>
        db
          .from('order_items')
          .select('name, unit_price, quantity, discount, orders!inner(status, paid_at)')
          .eq('client_id', clientId)
          .filter('orders.status', 'eq', 'paid')
          .filter('orders.paid_at', 'gte', thirtyAgo.toISOString())
          .order('id', { ascending: true })
          .range(from, to),
      ),

      db.from('ingredients').select('*').eq('client_id', clientId),

      db.from('staff').select('id, name').eq('client_id', clientId).order('name', { ascending: true }),

      db
        .from('staff_rates')
        .select('staff_id, pay_type, rate_amd, effective_from')
        .eq('client_id', clientId),

      db
        .from('time_entries')
        .select('staff_id, clock_in, clock_out, break_minutes, note')
        .eq('client_id', clientId)
        .gte('clock_in', todayStart.toISOString())
        .lt('clock_in', tomorrowStart.toISOString()),

      bookingsEnabled
        ? db
            .from('bookings')
            .select('id, guest_name, check_in, check_out, status')
            .eq('client_id', clientId)
            .lte('check_in', todayIso)
            .gte('check_out', todayIso)
        : Promise.resolve({ data: null, error: null }),

      db.from('products').select('name, categories!category_id(name)').eq('client_id', clientId),

      fetchPaged<{ name: string; unit_price: number; quantity: number; discount: number | null }>(
        (from, to) =>
          db
            .from('order_items')
            .select('name, unit_price, quantity, discount, orders!inner(status, paid_at)')
            .eq('client_id', clientId)
            .filter('orders.status', 'eq', 'paid')
            .filter('orders.paid_at', 'gte', todayStart.toISOString())
            .order('id', { ascending: true })
            .range(from, to),
      ),
    ],
  );

  const sideErr = staffRes.error ?? rateRes.error ?? entryRes.error ?? bookingRes.error ?? productRes.error;
  if (sideErr) throw new Error(sideErr.message);

  const toKpi = (
    rows: {
      total: number;
      pax: number | null;
      status: string;
      close_reason: string | null;
      tip_amount: number | null;
    }[],
  ): KpiValues => {
    const paid = rows.filter((o) => o.status === 'paid');
    return {
      revenue: paid.reduce((s, o) => s + Number(o.total), 0),
      orders: paid.length,
      pax: paid.reduce((s, o) => s + (o.pax ?? 1), 0),
      free: rows
        .filter((o) => o.close_reason === 'free')
        .reduce((s, o) => s + Number(o.total), 0),
      tips: paid.reduce((s, o) => s + (Number(o.tip_amount) > 0 ? Number(o.tip_amount) : 0), 0),
    };
  };

  const today = toKpi(todayRes.data ?? []);
  const yesterday = toKpi(yestRes.data ?? []);

  // ── 30-day trend — pre-fill every day with zero
  const trendMap = new Map<string, number>();
  for (let i = 29; i >= 0; i--) trendMap.set(dayLabel(dayStart(i), locale), 0);
  for (const o of trendRows) {
    if (!o.paid_at) continue;
    const lbl = dayLabel(new Date(o.paid_at), locale);
    // Ignore rows outside the seeded window — a stray label would otherwise
    // be appended as an extra bar at the end of the chart.
    if (!trendMap.has(lbl)) continue;
    trendMap.set(lbl, trendMap.get(lbl)! + Number(o.total));
  }
  const trend: DayBucket[] = Array.from(trendMap, ([label, revenue]) => ({
    label,
    revenue,
  }));

  // ── Hourly (last 7 days total)
  const hourMap = new Map<number, number>();
  for (let h = 0; h < 24; h++) hourMap.set(h, 0);
  for (const o of hourlyRows) {
    if (!o.paid_at) continue;
    const h = new Date(o.paid_at).getHours();
    hourMap.set(h, (hourMap.get(h) ?? 0) + 1);
  }
  const hourly: HourBucket[] = Array.from(hourMap, ([h, orders]) => ({
    label: String(h).padStart(2, '0'),
    orders,
  })).sort((a, b) => Number(a.label) - Number(b.label));

  // ── Payment split
  const pays = paymentRows;
  const cashAmt = pays.filter((p) => p.method === 'cash').reduce((s, p) => s + Number(p.amount), 0);
  const cardAmt = pays.filter((p) => p.method === 'card').reduce((s, p) => s + Number(p.amount), 0);
  const paymentSplit: PaySplit[] = (
    [
      { method: 'cash' as const, value: Math.round(cashAmt), color: 'var(--chart-6)' },
      { method: 'card' as const, value: Math.round(cardAmt), color: 'var(--chart-1)' },
    ] satisfies PaySplit[]
  ).filter((e) => e.value > 0);

  // ── Top products
  const prodMap = new Map<string, number>();
  for (const item of itemRows) {
    const rev = Number(item.unit_price) * Number(item.quantity) - Number(item.discount ?? 0);
    prodMap.set(item.name, (prodMap.get(item.name) ?? 0) + rev);
  }
  const topProducts: TopProduct[] = Array.from(prodMap, ([name, revenue]) => ({
    name,
    revenue: Math.round(revenue),
  }))
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 8);

  // ── Low stock
  const allIng = ingRes.data ?? [];
  const lowStock = allIng.filter(
    (i) => i.low_stock_threshold !== null && Number(i.stock) <= Number(i.low_stock_threshold),
  );

  const ratesById = new Map<string, RatePeriod[]>();
  for (const row of rateRes.data ?? []) {
    const list = ratesById.get(row.staff_id) ?? [];
    list.push({
      effectiveFrom: row.effective_from,
      payType: asPayType(row.pay_type),
      rateAmd: row.rate_amd,
    });
    ratesById.set(row.staff_id, list);
  }
  const punchesById = new Map<string, PunchSpan[]>();
  for (const row of entryRes.data ?? []) {
    const list = punchesById.get(row.staff_id) ?? [];
    if (row.clock_out && row.note !== 'off') {
      list.push({
        clockIn: new Date(row.clock_in),
        clockOut: new Date(row.clock_out),
        breakMinutes: row.break_minutes,
      });
    }
    punchesById.set(row.staff_id, list);
  }
  const now = new Date();
  const dayStaff: DayStaff[] = (staffRes.data ?? [])
    .filter((person) => punchesById.has(person.id))
    .map((person) => ({
      id: person.id,
      name: person.name,
      wageAmd: wageForRange(
        ratesById.get(person.id) ?? [],
        punchesById.get(person.id) ?? [],
        todayStart,
        tomorrowStart,
        now,
      ).wageAmd,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const activeBookings = (bookingRes.data ?? []).filter(
    (row) => row.status !== 'cancelled' && row.status !== 'rejected',
  );
  const dayBookings: DayBookings | null = bookingsEnabled
    ? {
        inHouse: activeBookings.filter((row) => row.check_in <= todayIso && row.check_out > todayIso).length,
        arriving: activeBookings.filter((row) => row.check_in === todayIso).length,
        leaving: activeBookings.filter((row) => row.check_out === todayIso).length,
        movement: activeBookings
          .flatMap((row) => {
            const moves: DayBookingMove[] = [];
            if (row.check_in === todayIso) {
              moves.push({
                id: `${row.id}-in`,
                name: row.guest_name,
                kind: 'arriving',
                pending: row.status === 'pending',
              });
            }
            if (row.check_out === todayIso) {
              moves.push({
                id: `${row.id}-out`,
                name: row.guest_name,
                kind: 'leaving',
                pending: row.status === 'pending',
              });
            }
            return moves;
          })
          .sort(
            (a, b) =>
              Number(a.kind === 'leaving') - Number(b.kind === 'leaving') || a.name.localeCompare(b.name),
          ),
      }
    : null;

  const nameToCat = new Map<string, string>();
  for (const product of (productRes.data ?? []) as unknown as Array<{
    name: string;
    categories: { name: string } | { name: string }[] | null;
  }>) {
    const linked = Array.isArray(product.categories) ? product.categories[0] : product.categories;
    if (linked?.name) nameToCat.set(product.name, linked.name);
  }
  const catTotals = new Map<string, number>();
  for (const item of todayItems) {
    const rev = Number(item.unit_price) * Number(item.quantity) - Number(item.discount ?? 0);
    if (rev <= 0) continue;
    const cat = nameToCat.get(item.name) ?? '';
    catTotals.set(cat, (catTotals.get(cat) ?? 0) + rev);
  }
  const categoryShare: CatShare[] = [...catTotals]
    .map(([name, value]) => ({ name, value: Math.round(value) }))
    .filter((row) => row.value > 0)
    .sort((a, b) => b.value - a.value);

  return {
    today,
    yesterday,
    trend,
    hourly,
    paymentSplit,
    topProducts,
    lowStock,
    dayStaff,
    dayBookings,
    categoryShare,
  };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function pctDelta(value: number, prev: number): number | null {
  return prev > 0 ? ((value - prev) / prev) * 100 : null;
}

function RevenueTrend({ data }: { data: DayBucket[] }) {
  const { t, i18n } = useTranslation();
  const AMD = (n: number) => formatAmd(n, i18n.language);
  return (
    <ResponsiveContainer width="100%" height={180}>
      <BarChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
        <XAxis dataKey="label" tickLine={false} axisLine={false} tick={CHART_TICK} interval={4} />
        <YAxis hide />
        <Tooltip
          formatter={(v: unknown) => [AMD(Number(v)), t('dashboard.revenueChart')]}
          contentStyle={CHART_TOOLTIP}
          cursor={CHART_CURSOR}
        />
        <Bar dataKey="revenue" fill="var(--info)" radius={[3, 3, 0, 0]} maxBarSize={22} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function HourlyChart({ data }: { data: HourBucket[] }) {
  const { t } = useTranslation();
  const maxOrders = Math.max(...data.map((d) => d.orders), 1);
  return (
    <ResponsiveContainer width="100%" height={140}>
      <BarChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
        <XAxis dataKey="label" tickLine={false} axisLine={false} tick={CHART_TICK} interval={2} />
        <YAxis hide />
        <Tooltip
          formatter={(v: unknown) => [String(v), t('dashboard.orders')]}
          contentStyle={CHART_TOOLTIP}
          cursor={CHART_CURSOR}
        />
        <Bar dataKey="orders" radius={[3, 3, 0, 0]} maxBarSize={18}>
          {data.map((entry, i) => (
            <Cell
              key={i}
              fill="var(--info)"
              fillOpacity={maxOrders > 0 ? 0.35 + (entry.orders / maxOrders) * 0.65 : 0.35}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function PaymentSplitChart({ data }: { data: PaySplit[] }) {
  const { t, i18n } = useTranslation();
  const AMD = (n: number) => formatAmd(n, i18n.language);
  const methodLabel = (m: PaySplit['method']) =>
    m === 'cash' ? t('dashboard.cash') : t('dashboard.card');

  if (data.length === 0) return <p className="chart-empty">{t('dashboard.noPayments30')}</p>;
  const total = data.reduce((s, d) => s + d.value, 0);
  return (
    <div>
      <ResponsiveContainer width="100%" height={150}>
        <PieChart>
          <Pie
            data={data.map((d) => ({ ...d, name: methodLabel(d.method) }))}
            cx="50%"
            cy="50%"
            innerRadius={42}
            outerRadius={66}
            dataKey="value"
            paddingAngle={3}
          >
            {data.map((e, i) => (
              <Cell key={i} fill={e.color} />
            ))}
          </Pie>
          <Tooltip formatter={(v: unknown) => [AMD(Number(v)), '']} contentStyle={CHART_TOOLTIP} />
        </PieChart>
      </ResponsiveContainer>
      <div className="pay-legend">
        {data.map((d) => (
          <div key={d.method} className="pay-legend-row">
            <span className="pay-legend-dot" style={{ background: d.color }} />
            <span className="pay-legend-name">{methodLabel(d.method)}</span>
            <span className="pay-legend-pct">
              {total > 0 ? Math.round((d.value / total) * 100) : 0}%
            </span>
            <span className="pay-legend-val">{AMD(d.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TopProductsList({ data }: { data: TopProduct[] }) {
  const { t, i18n } = useTranslation();
  const AMD = (n: number) => formatAmd(n, i18n.language);
  if (data.length === 0) return <p className="chart-empty">{t('dashboard.noSales30')}</p>;
  const max = Math.max(...data.map((d) => d.revenue), 1);
  const total = data.reduce((s, d) => s + d.revenue, 0);
  return (
    <div className="top-list">
      {data.map((p, i) => (
        <div key={p.name} className="top-row">
          <span className="top-rank">{i + 1}</span>
          <div className="top-info">
            <div className="top-name">{p.name}</div>
            <div className="top-bar-track">
              <div className="top-bar-fill" style={{ width: `${(p.revenue / max) * 100}%` }} />
            </div>
          </div>
          <div className="top-meta">
            <span className="top-revenue">{AMD(p.revenue)}</span>
            <span className="top-pct">{total > 0 ? Math.round((p.revenue / total) * 100) : 0}%</span>
          </div>
        </div>
      ))}
    </div>
  );
}


function StaffToday({ people }: { people: DayStaff[] }) {
  const { t, i18n } = useTranslation();
  const AMD = (n: number) => formatAmd(n, i18n.language);
  const total = people.reduce((sum, person) => sum + person.wageAmd, 0);
  return (
    <Card className="dash-mini">
      <CardHeader
        title={t('dashboard.staffToday')}
        right={
          <span className="dash-mini-total">
            <span className="dash-mini-aside">{t('dashboard.staffWage')}</span>
            {AMD(total)}
          </span>
        }
      />
      {people.length === 0 ? (
        <p className="dash-mini-empty">{t('dashboard.staffTodayEmpty')}</p>
      ) : (
        <div className="dash-chips">
          {people.map((person, index) => (
            <span key={person.id} className="dash-chip" style={{ ['--chip' as string]: chartColor(index) }}>
              <span className="dash-chip-dot" />
              <span className="dash-chip-name">{person.name}</span>
              <span className="dash-chip-val">{AMD(person.wageAmd)}</span>
            </span>
          ))}
        </div>
      )}
    </Card>
  );
}

function BookingsToday({ data }: { data: DayBookings }) {
  const { t } = useTranslation();
  const counts = [
    { key: 'in', n: data.inHouse, label: t('dashboard.bookingsInHouse'), color: 'var(--chart-6)' },
    { key: 'arr', n: data.arriving, label: t('dashboard.bookingsArriving'), color: 'var(--chart-1)' },
    { key: 'out', n: data.leaving, label: t('dashboard.bookingsLeaving'), color: 'var(--chart-3)' },
  ];
  return (
    <Card className="dash-mini">
      <CardHeader title={t('dashboard.bookingsToday')} />
      <div className="dash-chips">
        {counts.map((item) => (
          <span key={item.key} className="dash-chip" style={{ ['--chip' as string]: item.color }}>
            <span className="dash-chip-val">{item.n}</span>
            <span className="dash-chip-name">{item.label}</span>
          </span>
        ))}
        {data.movement.map((stay) => (
          <span
            key={stay.id}
            className="dash-chip"
            style={{ ['--chip' as string]: stay.kind === 'arriving' ? 'var(--chart-1)' : 'var(--chart-3)' }}
          >
            <span className="dash-chip-dot" />
            <span className="dash-chip-name">{stay.name}</span>
            <span className="dash-chip-note">
              {stay.pending
                ? t('dashboard.bookingsPending')
                : stay.kind === 'arriving'
                  ? t('dashboard.bookingsArriving')
                  : t('dashboard.bookingsLeaving')}
            </span>
          </span>
        ))}
      </div>
    </Card>
  );
}

function CategoryShare({ data }: { data: CatShare[] }) {
  const { t } = useTranslation();
  const label = (name: string) => name || t('dashboard.uncategorised');
  const total = data.reduce((sum, row) => sum + row.value, 0);
  return (
    <Card className="dash-mini">
      <CardHeader title={t('dashboard.categoryShare')} />
      {data.length === 0 ? (
        <p className="dash-mini-empty">{t('dashboard.noCategorySales')}</p>
      ) : (
        <div className="dash-chips">
          {data.map((row, index) => (
            <span
              key={row.name || 'none'}
              className="dash-chip"
              style={{ ['--chip' as string]: chartColor(index) }}
            >
              <span className="dash-chip-dot" />
              <span className="dash-chip-name">{label(row.name)}</span>
              <span className="dash-chip-val">
                {total > 0 ? Math.round((row.value / total) * 100) : 0}%
              </span>
            </span>
          ))}
        </div>
      )}
    </Card>
  );
}

// ─── Main view ────────────────────────────────────────────────────────────────

export function DashboardView({
  clientId,
  bookingsEnabled = false,
  onNavigate,
}: {
  clientId: string | null;
  bookingsEnabled?: boolean;
  onNavigate?: (view: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = resolveAppLocale(i18n.language);
  const AMD = (n: number) => formatAmd(n, i18n.language);
  const [state, setState] = useState<DashState>(INITIAL);
  const figuresReady = !state.loading && !state.error;
  const aovNow = state.today.orders > 0 ? state.today.revenue / state.today.orders : 0;
  const revenueN = useAnimatedNumber(state.today.revenue, figuresReady);
  const ordersN = useAnimatedNumber(state.today.orders, figuresReady);
  const aovN = useAnimatedNumber(aovNow, figuresReady);
  const paxN = useAnimatedNumber(state.today.pax, figuresReady);
  const freeN = useAnimatedNumber(state.today.free, figuresReady);
  const tipsN = useAnimatedNumber(state.today.tips, figuresReady);

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    setState(INITIAL);
    void fetchDashboard(clientId, locale, bookingsEnabled)
      .then((data) => {
        if (!cancelled) setState({ loading: false, error: null, ...data });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState((s) => ({
          ...s,
          loading: false,
          error: err instanceof Error ? err.message : t('dashboard.loadFailed'),
        }));
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, locale, bookingsEnabled, t]);

  useEffect(() => {
    if (!supabase || !clientId) return;
    const client = supabase;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;
    const reload = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        void fetchDashboard(clientId, locale, bookingsEnabled)
          .then((data) => {
            if (!cancelled) setState({ loading: false, error: null, ...data });
          })
          .catch((err: unknown) => {
            if (cancelled) return;
            setState((s) => ({
              ...s,
              loading: false,
              error: err instanceof Error ? err.message : t('dashboard.loadFailed'),
            }));
          });
      }, 400);
    };
    // Only orders and payments are in the realtime publication. Listening to
    // other tables fails the whole channel, so the home screen never reloads.
    const channel = client
      .channel(`dashboard-live-${clientId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'orders', filter: `client_id=eq.${clientId}` },
        reload,
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'payments', filter: `client_id=eq.${clientId}` },
        reload,
      )
      .subscribe();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      void client.removeChannel(channel);
    };
  }, [clientId, locale, bookingsEnabled, t]);

  const todayLabel = new Date().toLocaleDateString(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  if (state.loading) {
    return (
      <div className="dash-loading">
        <div className="spinner" />
        {t('dashboard.loading')}
      </div>
    );
  }

  if (state.error) {
    return (
      <div>
        <PageHeader title={t('dashboard.title')} subtitle={todayLabel} />
        <Callout tone="danger" title={t('dashboard.couldntLoad')}>
          {state.error}
        </Callout>
      </div>
    );
  }

  const { today, yesterday, trend, hourly, paymentSplit, topProducts, lowStock, dayStaff, dayBookings, categoryShare } =
    state;
  const aovY = yesterday.orders > 0 ? yesterday.revenue / yesterday.orders : 0;

  return (
    <div className="dashboard">
      <PageHeader title={t('dashboard.title')} subtitle={todayLabel} />

      <Stats>
        <Stat
          icon={<Wallet />}
          label={t('dashboard.revenueToday')}
          value={AMD(Math.round(revenueN))}
          deltaPct={pctDelta(today.revenue, yesterday.revenue)}
          deltaLabel={t('dashboard.vsYesterday')}
        />
        <Stat
          icon={<Receipt />}
          label={t('dashboard.ordersToday')}
          value={Math.round(ordersN)}
          deltaPct={pctDelta(today.orders, yesterday.orders)}
          deltaLabel={t('dashboard.vsYesterday')}
        />
        <Stat
          icon={<BarChart3 />}
          label={t('dashboard.averageOrder')}
          value={AMD(Math.round(aovN))}
          deltaPct={pctDelta(aovNow, aovY)}
          deltaLabel={t('dashboard.vsYesterday')}
        />
        <Stat
          icon={<Users />}
          label={t('dashboard.coversToday')}
          value={Math.round(paxN)}
          deltaPct={pctDelta(today.pax, yesterday.pax)}
          deltaLabel={t('dashboard.vsYesterday')}
        />
        <Stat
          icon={<CreditCard />}
          label={t('dashboard.tipsToday')}
          value={AMD(Math.round(tipsN))}
          deltaPct={pctDelta(today.tips, yesterday.tips)}
          deltaLabel={t('dashboard.vsYesterday')}
        />
        <Stat
          icon={<Gift />}
          label={t('dashboard.givenAwayToday')}
          value={AMD(Math.round(freeN))}
          deltaPct={pctDelta(today.free, yesterday.free)}
          invertDelta
          deltaLabel={t('dashboard.vsYesterday')}
        />
      </Stats>

      <div className="dash-today">
        <StaffToday people={dayStaff} />
        {dayBookings && <BookingsToday data={dayBookings} />}
        <CategoryShare data={categoryShare} />
      </div>

      <div className="dash-grid">
        <Card>
          <CardHeader title={t('dashboard.revenueChart')} caption={t('dashboard.last30')} />
          <RevenueTrend data={trend} />
        </Card>

        <Card>
          <CardHeader title={t('dashboard.paymentSplit')} caption={t('dashboard.last30')} />
          <PaymentSplitChart data={paymentSplit} />
        </Card>

        <Card>
          <CardHeader title={t('dashboard.ordersByHour')} caption={t('dashboard.last7')} />
          <HourlyChart data={hourly} />
        </Card>

        <Card>
          <CardHeader title={t('dashboard.topProducts')} caption={t('dashboard.topProductsCaption')} />
          <TopProductsList data={topProducts} />
        </Card>
      </div>

      <LowStockPanel
        ingredients={lowStock}
        action={
          onNavigate && (
            <Button variant="secondary" size="sm" onClick={() => onNavigate('stock-in')}>
              {t('dashboard.stockIn')}
              <ArrowRight />
            </Button>
          )
        }
      />
    </div>
  );
}
